/**
 * Shared roster plan state machine hook (Bible § B.5.3 + ROSTER_PLAN_SPEC § B.5.7).
 * Drop-shuffle plan flow for FA pickup requiring roster drop.
 *
 * Pure state machine + API wrapper — no UI coupling.
 * Per 9o.5 (Option F.4): formally promoted from
 *   pages/league-dashboard/free-agent-search/hooks/useRosterPlan.js
 * to shared hooks/rosterPlan/ so mobile and desktop both consume the same canonical implementation.
 *
 * Side effects:
 *   - Dispatches 'roster-updated' window event on successful executePlan (Bible § B.5.6)
 *
 * State machine:
 *   idle → select_drop → loading_plans → choose_plan → confirming → done | error | cancelled
 *
 * Owns:
 *   - API calls to /plan-add and /execute-plan via leaguesAPI
 *   - selectedDropPlayer, selectedPlan, plans, error state
 *   - roster-updated event dispatch on successful execute
 *
 * The RosterPlanModal / MobileRosterPlanModal components render state from this hook.
 * No API calls in the modals. Mirrors usePlayerAssignment pattern.
 */

import { useCallback, useState } from 'react';
import { leaguesAPI } from '../../services/apiService';

/**
 * @param {Object} args
 * @param {string} args.leagueId
 * @param {Object} args.candidate - Canonical player object (FA being added).
 *   Must have ids.mlb populated; ids.mlb is sent to the backend as
 *   candidate_player_id.
 * @param {Array} args.currentRoster - User's current roster (canonical
 *   player objects). Used to look up the chosen drop by mlb id.
 * @param {boolean} [args.isCommissionerMode]
 * @param {Function|null} [args.getTargetTeamId] - Returns target team_id
 *   when in commissioner mode. Signature: (fallback) => string|null.
 */
export function useRosterPlan({
  leagueId,
  candidate,
  currentRoster,
  isCommissionerMode = false,
  getTargetTeamId = null,
}) {
  const [state, setState] = useState('idle');
  const [plans, setPlans] = useState([]);
  const [selectedDropPlayer, setSelectedDropPlayer] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [errorClass, setErrorClass] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [infeasibleReason, setInfeasibleReason] = useState(null);

  const reset = useCallback(() => {
    setState('idle');
    setPlans([]);
    setSelectedDropPlayer(null);
    setSelectedPlan(null);
    setErrorClass(null);
    setErrorMessage(null);
    setInfeasibleReason(null);
  }, []);

  // Open the modal in select_drop state
  const open = useCallback(() => {
    setPlans([]);
    setSelectedDropPlayer(null);
    setSelectedPlan(null);
    setErrorClass(null);
    setErrorMessage(null);
    setInfeasibleReason(null);
    setState('select_drop');
  }, []);

  // User clicked Cancel
  const cancel = useCallback(() => {
    setState('cancelled');
  }, []);

  // User picked a drop player; fetch plans for that drop.
  // dropPlayerMlbId is the MLB player ID (int), matching backend contract.
  const requestPlans = useCallback(async (dropPlayerMlbId) => {
    const dropPlayer = (currentRoster || []).find(
      (p) => p?.ids?.mlb === dropPlayerMlbId
    );
    setSelectedDropPlayer(dropPlayer || null);
    setState('loading_plans');
    setErrorClass(null);
    setErrorMessage(null);
    setInfeasibleReason(null);
    setPlans([]);

    try {
      const targetTeamId =
        isCommissionerMode && typeof getTargetTeamId === 'function'
          ? getTargetTeamId(null)
          : null;

      const result = await leaguesAPI.generatePlanForDrop(leagueId, {
        candidate_player_id: candidate?.ids?.mlb,
        drop_player_id: dropPlayerMlbId,
        commissioner_action: !!isCommissionerMode,
        target_team_id: targetTeamId,
      });

      if (result?.success && Array.isArray(result.plans) && result.plans.length > 0) {
        setPlans(result.plans);
        // Auto-select first plan per § B.0.3 bullet 4 default
        setSelectedPlan(result.plans[0]);
        setState('choose_plan');
      } else if (result?.success) {
        // success=true but no plans → infeasible
        setInfeasibleReason(result.infeasible_reason || 'infeasible_no_shuffle');
        setState('choose_plan');
      } else {
        setErrorClass(result?.error_class || 'database_error');
        setErrorMessage(result?.error_message || 'Failed to generate plans');
        setState('error');
      }
    } catch (err) {
      const errResp = err?.response?.data;
      setErrorClass(errResp?.error_class || 'database_error');
      setErrorMessage(
        errResp?.error_message ||
          errResp?.message ||
          err?.message ||
          'Failed to generate plans'
      );
      setState('error');
    }
  }, [leagueId, candidate, currentRoster, isCommissionerMode, getTargetTeamId]);

  // User wants to change the drop choice (back to select_drop)
  const changeDrop = useCallback(() => {
    setSelectedDropPlayer(null);
    setPlans([]);
    setSelectedPlan(null);
    setInfeasibleReason(null);
    setErrorClass(null);
    setErrorMessage(null);
    setState('select_drop');
  }, []);

  // User picked a different plan from the radio list
  const selectPlan = useCallback((plan) => {
    setSelectedPlan(plan);
  }, []);

  // User confirms; execute the selected plan via /execute-plan
  const executePlan = useCallback(async () => {
    if (!selectedDropPlayer || !selectedPlan) return null;

    setState('confirming');
    setErrorClass(null);
    setErrorMessage(null);

    try {
      const targetTeamId =
        isCommissionerMode && typeof getTargetTeamId === 'function'
          ? getTargetTeamId(null)
          : null;

      const result = await leaguesAPI.executePlan(leagueId, {
        candidate_player_id: candidate?.ids?.mlb,
        drop_player_id: selectedDropPlayer?.ids?.mlb,
        plan_fingerprint: selectedPlan.plan_fingerprint,
        commissioner_action: !!isCommissionerMode,
        target_team_id: targetTeamId,
      });

      if (result?.success) {
        // Dispatch roster-updated per § B.5.6
        if (result.should_dispatch_roster_updated !== false) {
          window.dispatchEvent(new Event('roster-updated'));
        }
        setState('done');
        return result;
      }

      setErrorClass(result?.error_class || 'database_error');
      setErrorMessage(result?.error_message || 'Failed to execute plan');
      setState('error');
      return null;
    } catch (err) {
      const errResp = err?.response?.data;
      setErrorClass(errResp?.error_class || 'database_error');
      setErrorMessage(
        errResp?.error_message ||
          errResp?.message ||
          err?.message ||
          'Failed to execute plan'
      );
      setState('error');
      return null;
    }
  }, [leagueId, candidate, selectedDropPlayer, selectedPlan, isCommissionerMode, getTargetTeamId]);

  // Retry from error state. For database_error: retry the same drop.
  // For other classes: surface select_drop so user can pick differently.
  const retry = useCallback(() => {
    if (errorClass === 'database_error' && selectedDropPlayer?.ids?.mlb) {
      requestPlans(selectedDropPlayer.ids.mlb);
    } else {
      changeDrop();
    }
  }, [errorClass, selectedDropPlayer, requestPlans, changeDrop]);

  return {
    // State
    state,
    plans,
    selectedDropPlayer,
    selectedPlan,
    errorClass,
    errorMessage,
    infeasibleReason,

    // Actions
    open,
    cancel,
    requestPlans,
    changeDrop,
    selectPlan,
    executePlan,
    retry,
    reset,
  };
}
